// The page DJI Pilot 2 opens for its cloud service, as plain TypeScript
// (autrik_codex T90): config, Sentry, the login (T90.2) and the two routes,
// by path: / is the login, /djihome the home (home.ts). Pilot 2 reopens the
// page at the last URL it saw, so both start cold.

import './style.css';
import { type Config, loadConfig } from './config';
import { whoami } from './api';
import { LoginError, login, logout } from './auth';
import { endPilotToken } from './pilot_token';
import { initSentry, reportError } from './report';
import { home } from './home';
import { banner, button, el, field, formRow, icon, panel, stateView } from './ui';

const app = document.getElementById('app')!;
document.body.classList.add('ac');
const HOME = '/djihome';

function go(path: string): void {
  if (location.pathname !== path) history.replaceState(null, '', path + location.hash);
}

/** An error state, centred on the page: what failed, and what to do. */
function card(title: string, body: string): void {
  const wrap = el('div', 'login');
  wrap.append(stateView(title, body, { error: true, action: button('Reload', () => location.reload()) }));
  app.replaceChildren(wrap);
}

/**
 * Ends the Kratos session and the token Pilot's modules were given; the
 * modules stay loaded (as the Flutter page left them) and are refused from
 * then on.
 */
function logoutButton(cfg: Config): HTMLElement {
  const b = button('Log Out', () => {
    b.disabled = true;
    endPilotToken(cfg)
      .catch((e) => reportError(e, 'logout: revoke-previous-session'))
      .then(() => logout(cfg))
      .catch((e) => reportError(e, 'logout'))
      .finally(() => location.replace('/'));
  });
  return b;
}

/**
 * The login: a single centred panel on the dark ground, whatever the theme
 * (design system screens.md, "Outside the shell"). Resolves once Kratos has
 * accepted the password.
 */
function loginForm(cfg: Config): Promise<void> {
  return new Promise((resolve) => {
    document.documentElement.dataset['theme'] = 'dark';
    const form = el('form', 'login');
    const p = panel();
    const email = el('input');
    email.type = 'email';
    email.name = 'email';
    email.autocomplete = 'username';
    email.required = true;
    const password = el('input');
    password.type = 'password';
    password.name = 'password';
    password.autocomplete = 'current-password';
    password.required = true;
    const error = banner('error');
    const submit = el('button', 'ac-btn ac-btn--filled ac-btn--md', 'Sign In');
    submit.type = 'submit';
    const brand = el('div', 'brand');
    brand.append(icon('drone', 'ac-icon--20'), el('span', 'ac-t-titleLarge', 'Autrik DJI'));
    const actions = el('div', 'actions');
    actions.append(submit);
    p.append(
      brand,
      el('div', 'ac-dim', 'Sign in with your Autrik account.'),
      formRow('Email', field(email)),
      formRow('Password', field(password)),
      error.el,
      actions,
      el('div', 'version ac-t-bodySmall ac-dim', `AutrikOS DJI ${cfg.appVersion}`),
    );
    form.append(p);
    form.addEventListener('submit', (ev) => {
      ev.preventDefault();
      error.set('');
      submit.disabled = true;
      submit.textContent = 'Signing In…';
      login(cfg, email.value.trim(), password.value)
        .then(() => {
          delete document.documentElement.dataset['theme'];
          resolve();
        })
        .catch((e: unknown) => {
          // A wrong password is Kratos' 400; anything else is reported.
          if (!(e instanceof LoginError && e.status === 400)) reportError(e, 'login');
          error.set(e instanceof Error ? e.message : String(e));
          submit.disabled = false;
          submit.textContent = 'Sign In';
        });
    });
    app.replaceChildren(form);
    email.focus();
  });
}

async function start(): Promise<void> {
  let cfg: Config;
  try {
    cfg = await loadConfig();
  } catch (e) {
    card('The page could not load its settings', `${String(e)}. Reload the page; if it stays, this release lacks its config.json.`);
    return;
  }
  initSentry(cfg);

  let session = await whoami(cfg).catch((e) => {
    reportError(e, 'whoami');
    return null;
  });
  if (!session) {
    go('/');
    await loginForm(cfg);
    session = await whoami(cfg);
    if (!session) {
      card('Signed in, but no session arrived', 'The login was accepted, but the session cookie did not come back. Reload the page.');
      return;
    }
  }
  go(HOME);
  await home(cfg, session, () => logoutButton(cfg));
}

start().catch((e) => {
  reportError(e, 'start');
  card('The page failed to start', `${String(e)}. Reload the page.`);
});
